import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { ConfiguracaoDeExtratoInput } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { campo, moldura, rotulo } from "../estilos";
import { CamposDaCobranca } from "./CamposDaCobranca";
import { PreviaDoExtrato } from "./PreviaDoExtrato";
import { RegrasDosClientes } from "./RegrasDosClientes";


/** Financeiro › Configurações: como o laboratório cobra (o padrão), as exceções de cada
 *  cliente e o que vai no documento. Salvar a primeira vez liga os extratos. */
export function ExtratosConfig() {
  const utils = trpc.useUtils();
  const pode = usePermissao("finance.manage");
  const dados = trpc.financeiro.configExtrato.ler.useQuery().data;
  const [form, setForm] = useState<ConfiguracaoDeExtratoInput | null>(null);
  useEffect(() => {
    if (!dados) return;
    const { laboratorio: _l, ativo: _a, documento: _d, ...resto } = dados;
    setForm(resto);
  }, [dados]);

  const salvar = trpc.financeiro.configExtrato.salvar.useMutation({
    onSuccess: () => {
      toast.success(dados?.ativo ? "Configuração dos extratos salva." : "Extratos ligados.");
      utils.financeiro.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  if (!dados || !form) return null;
  const mudar = (parcial: Partial<ConfiguracaoDeExtratoInput>) => setForm({ ...form, ...parcial });

  return (
    <section className={`${moldura} overflow-hidden`}>
      <header className="border-b border-zinc-100 bg-zinc-50/60 px-4 py-3 dark:border-border dark:bg-muted/30">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Extratos de cobrança</h3>
        <p className="text-xs text-zinc-500 dark:text-muted-foreground">
          {dados.ativo
            ? "O que foi entregue vira extrato: numerado, com a sua marca e as formas de pagamento."
            : "Ligue para cobrar com extrato: numerado, com a sua marca, por clínica ou dentista, na frequência de cada cliente. Até lá, as Cobranças seguem por mês."}
        </p>
      </header>

      <div className="grid gap-6 p-4 lg:grid-cols-2">
        <fieldset disabled={!pode} className="space-y-5">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Cobrança padrão</p>
            <CamposDaCobranca comPadrao={false} valor={form} onChange={(v) => mudar(v)} />
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Início</p>
            <label className="block sm:w-1/2">
              <span className={rotulo}>Extratos a partir de</span>
              <input type="month" value={form.inicioDosExtratos} onChange={(e) => mudar({ inicioDosExtratos: e.target.value })} className={campo} />
              <span className="mt-1 block text-[11px] text-zinc-500 dark:text-muted-foreground">Entregas antes deste mês seguem nas Cobranças por mês. Não muda depois do primeiro extrato.</span>
            </label>
          </div>

          {pode && (
            <div className="flex justify-end">
              <button
                type="button"
                disabled={salvar.isPending}
                onClick={() => salvar.mutate(form)}
                className="inline-flex h-10 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
              >
                {salvar.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {dados.ativo ? "Salvar" : "Ligar os extratos"}
              </button>
            </div>
          )}
        </fieldset>

        <div className="space-y-2">
          <PreviaDoExtrato laboratorio={dados.laboratorio} mostrarRefacaoGratuita={dados.documento.mostrarRefacaoGratuita} cabecalho={dados.documento} />
          {dados.ativo && <p className="text-xs text-zinc-500 dark:text-muted-foreground">Contatos, formas de pagamento e colunas do documento ficam logo abaixo.</p>}
        </div>
      </div>

      {dados.ativo && <RegrasDosClientes padrao={form} pode={pode} />}
    </section>
  );
}
